import React, { FC, Fragment, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';

import { useNotify, useGlobalClient } from 'hooks';
import { Login } from 'Links';
import {
  ClientLogo,
  Layouts,
  Loaders,
  PasswordRequirements,
  ValidatedTextFieldNext,
  VisibilityAdornment,
} from 'components';
import { getAppImage } from 'helpers';

import { ForgotPasswordSubmitSchema } from '../validations';
import { AuthContainerProps } from '../types';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordSubmit: FC<AuthContainerProps> = ({ authData }) => {
  const notify = useNotify();
  const client = useGlobalClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [working, setWorking] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const initialValues = {
    code: '',
    password: '',
  };

  async function handleSubmit(values: typeof initialValues) {
    setWorking(true);

    try {
      await Auth.forgotPasswordSubmit(authData.username, values.code, values.password);
    } catch (error) {
      try {
        // Maybe the user never confirmed their account?
        await Auth.confirmSignUp(authData.username, values.code);
        await Auth.signIn(authData.username, authData.password);
        navigate('/');
      } catch (e) {
        notify.error({ message: error.message });
        setWorking(false);
      }
    }
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <Fragment>
        <ClientLogo client={client} />
        <Typography variant="h2" align="center">
          {t('auth:forgotPasswordSidebarHeader')}
        </Typography>
        <Formik
          key="submit"
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={ForgotPasswordSubmitSchema}
        >
          <Layouts.OnboardingBox component={Form}>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                enableTrim
                fullWidth
                name="code"
                type="text"
                label={t('auth:forgotPasswordCode')}
                placeholder={t('auth:forgotPasswordCode')}
              />
            </Box>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                enableTrim
                disableError
                fullWidth
                name="password"
                type={passwordVisible ? 'text' : 'password'}
                label={t('auth:forgotPasswordNewPassword')}
                placeholder={t('auth:forgotPasswordNewPassword')}
                InputProps={{
                  endAdornment: (
                    <VisibilityAdornment
                      visible={passwordVisible}
                      onToggle={() => setPasswordVisible(!passwordVisible)}
                    />
                  ),
                }}
              />
              <Box marginTop={2}>
                <PasswordRequirements name="password" />
              </Box>
            </Box>
            <Box paddingTop={3}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={working}
              >
                {working ? <Loaders.Standard height={26} /> : t('auth:forgotPasswordSubmitButton')}
              </Button>
            </Box>
            <Box paddingTop={5} textAlign="center">
              <Link color="textSecondary" component={Login}>
                {t('auth:backToLogin')}
              </Link>
            </Box>
          </Layouts.OnboardingBox>
        </Formik>
      </Fragment>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default ForgotPasswordSubmit;
